import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { 
  LayoutDashboard,
  Filter, 
  RefreshCw, 
  BarChart3,
  FlaskConical,
  Building2,
  Calendar,
  LogOut,
  User
} from 'lucide-react';
import AdminDashboard from './AdminDashboard';
import AdminPage from './AdminPage'; // L'ancien composant de filtrage
import AdminSync from './AdminSync';
import AdminAPITest from './AdminAPITest';
import AdminVillas from './AdminVillas';
import AdminAvailability from './AdminAvailability';
import { filteringRulesService, villasService } from '../services/api';

const AdminPageNew = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({
    filteringRules: {
      total_rules: 0,
      active_rules: 0,
      inclusion_rules: 0,
      exclusion_rules: 0
    },
    database: {
      totalVillas: 0,
      filteredVillas: 0
    },
    sync: {
      lastSync: null
    },
    alerts: []
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      // Charger les stats des règles de filtrage
      const rulesStatsResponse = await filteringRulesService.getStats();
      if (rulesStatsResponse.success) {
        setStats(prev => ({
          ...prev,
          filteringRules: rulesStatsResponse.data
        }));
      }

      // Charger les stats des villas
      const villasResponse = await villasService.getVillas({ limit: 1 });
      if (villasResponse.success) {
        setStats(prev => ({
          ...prev,
          database: {
            totalVillas: villasResponse.total || 0,
            filteredVillas: villasResponse.filtered ? villasResponse.total : 0
          }
        }));
      }

      // TODO: Charger les stats de sync
      // const syncResponse = await fetch('/api/sync/status');
      
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const tabs = [
    {
      id: 'dashboard',
      label: 'Tableau de Bord',
      icon: LayoutDashboard,
      component: <AdminDashboard stats={stats} onNavigate={setActiveTab} />
    },
    {
      id: 'villas',
      label: 'Gestion Villas',
      icon: Building2,
      component: <AdminVillas />
    },
    {
      id: 'availability',
      label: 'Gestion Dispo',
      icon: Calendar,
      component: <AdminAvailability />
    },
    {
      id: 'filtering',
      label: 'Règles de Filtrage',
      icon: Filter,
      component: <AdminPage />
    },
    {
      id: 'sync',
      label: 'Synchronisation',
      icon: RefreshCw,
      component: <AdminSync />
    },
    {
      id: 'api-test',
      label: 'Tests API',
      icon: FlaskConical,
      component: <AdminAPITest />
    },
    {
      id: 'stats',
      label: 'Statistiques',
      icon: BarChart3,
      component: (
        <div className="p-8 text-center text-gray-500">
          <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <p className="text-lg font-medium">Statistiques détaillées</p>
          <p className="text-sm mt-2">Bientôt disponible</p>
        </div>
      )
    }
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header avec info utilisateur */}
        <Card className="mb-6 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-light text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Administration PRIVIA Villas
              </h1>
              {user && (
                <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                  <User className="w-4 h-4" />
                  <span>{user.full_name}</span>
                  {user.role === 'super_admin' && (
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
                      Super Admin
                    </span>
                  )}
                </div>
              )}
            </div>
            <Button 
              variant="outline"
              onClick={handleLogout}
              className="flex items-center gap-2 text-gray-700 hover:text-red-600 hover:border-red-600"
            >
              <LogOut className="w-4 h-4" />
              <span>Déconnexion</span>
            </Button>
          </div>
        </Card>

        {/* Navigation Tabs */}
        <Card className="mb-6 p-2">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <Button
                  key={tab.id}
                  variant={activeTab === tab.id ? 'default' : 'ghost'}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 transition-all
                    ${activeTab === tab.id 
                      ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-white' 
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </Button>
              );
            })}
          </div>
        </Card>

        {/* Content */}
        <div>
          {tabs.find(tab => tab.id === activeTab)?.component}
        </div>
      </div>
    </div>
  );
};

export default AdminPageNew;

